// Componente para seleção de CNAE

import React from "react";
import { HiBriefcase } from "react-icons/hi";
import { CNAEInfo } from "../../../types/orcamento-fornecedor.types";

interface SelecaoCnaeProps {
  cnaes: CNAEInfo[];
  selectedIds: string[];
  onChange: (cnaeIds: string[]) => void;
  error?: string;
}

/**
 * Seleção de um ou mais CNAEs. Um orçamento pode ter itens de atividades
 * diferentes, e cada um precisa de um CNAE compatível na nota fiscal.
 */
export const SelecaoCnae: React.FC<SelecaoCnaeProps> = ({
  cnaes,
  selectedIds,
  onChange,
  error,
}) => {
  // A lista sai na ordem dos CNAEs da empresa, e não na ordem dos cliques,
  // para o PDF e o FACITE não mudarem conforme o fornecedor marcou.
  const alternar = (id: string) => {
    const marcados = new Set(selectedIds);
    if (marcados.has(id)) marcados.delete(id);
    else marcados.add(id);
    onChange(cnaes.map((c) => c.id).filter((cid) => marcados.has(cid)));
  };

  return (
    <div className="border-b border-gray-200 pb-6">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
        <div className="w-10 h-10 rounded-lg bg-[#4760e0]/10 flex items-center justify-center">
          <HiBriefcase className="h-6 w-6 text-[#4760e0]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#161519]">
            Atividade Econômica (CNAE)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Selecione as atividades que contemplam os itens deste orçamento.
            Marque mais de uma se os itens forem de atividades diferentes.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-900 leading-relaxed">
          <strong>Importante:</strong> Cada código CNAE selecionado deve ser
          compatível com o serviço que será prestado e também adequado para a
          emissão da Nota Fiscal correspondente. O ISSQN informado na Nota
          Fiscal precisa estar coerente com o tipo de serviço executado.
          Divergências entre o serviço prestado, o CNAE e o ISSQN podem impedir
          o aceite da nota e o processamento do pagamento.
        </p>
      </div>

      <div className="space-y-3">
        {cnaes.map((cnae) => (
          <label
            key={cnae.id}
            className={`cursor-pointer block ${error ? "animate-shake" : ""}`}
          >
            <div
              className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all hover:border-[#4760e0] hover:bg-[#4760e0]/5 ${
                selectedIds.includes(cnae.id)
                  ? "border-[#4760e0] bg-[#4760e0]/10"
                  : "border-gray-200"
              } ${error ? "border-red-500" : ""}`}
            >
              <input
                type="checkbox"
                name="cnae"
                value={cnae.id}
                checked={selectedIds.includes(cnae.id)}
                onChange={() => alternar(cnae.id)}
                className="mt-1 shrink-0 w-4 h-4 text-[#4760e0] focus:ring-[#4760e0]"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#4760e0] font-mono">
                  {cnae.codigo}
                </div>
                <div className="text-sm mt-1 text-gray-700">
                  {cnae.descricao}
                </div>
              </div>
            </div>
          </label>
        ))}
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
    </div>
  );
};
